// Orbe de status da Clara — reconstrução fiel do "Ícone SVG de IA em escuta
// ativa" (zip no doc-storage, spec em SPEC-LLM.md): esfera glass com gradiente
// radial + brilho elíptico, 4 modos animados. Animações 100% SMIL (sem CSS
// keyframes, sem JS) — os .svg do zip vieram com o SMIL removido, então os
// <animate> abaixo seguem a spec à letra. IDs via useId: a sidebar renderiza
// o mesmo conteúdo em duas árvores (desktop + drawer mobile) e IDs fixos
// duplicariam no DOM.
import { useId } from 'react'

export type ClaraOrbMode = 'idle' | 'gravando' | 'working' | 'talking' | 'recusado'

export function ClaraOrb({ mode, className }: { mode: ClaraOrbMode; className?: string }) {
  const uid = useId()
  const grad = `orb-g-${uid}`
  const blur = `orb-b-${uid}`
  const clip = `orb-c-${uid}`

  return (
    <svg viewBox="0 0 96 96" className={className} aria-hidden="true">
      <defs>
        {mode === 'idle' && (
          <radialGradient id={grad} cx="35%" cy="30%" r="80%">
            <stop offset="0" stopColor="#9ca3af" /><stop offset=".45" stopColor="#6b7280" /><stop offset="1" stopColor="#4b5563" />
          </radialGradient>
        )}
        {mode === 'gravando' && (
          <radialGradient id={grad} cx="35%" cy="30%" r="80%">
            <stop offset="0" stopColor="#a7f3d0" /><stop offset=".45" stopColor="#34d399" /><stop offset="1" stopColor="#059669" />
          </radialGradient>
        )}
        {mode === 'working' && (
          <radialGradient id={grad} cx="35%" cy="30%" r="80%">
            <stop offset="0" stopColor="#fde68a" /><stop offset=".45" stopColor="#fbbf24" /><stop offset="1" stopColor="#f59e0b" />
          </radialGradient>
        )}
        {/* recusado: vermelho (pedido 2026-07-18, revisado no mesmo dia —
            era lima): comando descartado/não entendido/ruído */}
        {mode === 'recusado' && (
          <radialGradient id={grad} cx="35%" cy="30%" r="80%">
            <stop offset="0" stopColor="#fecaca" /><stop offset=".45" stopColor="#f87171" /><stop offset="1" stopColor="#dc2626" />
          </radialGradient>
        )}
        {mode === 'talking' && (
          <radialGradient id={grad} cx="35%" cy="30%" r="80%">
            <stop offset="0" stopColor="#7dd3fc">
              <animate attributeName="stop-color" values="#7dd3fc;#c4b5fd;#67e8f9;#7dd3fc" dur="5s" repeatCount="indefinite" />
            </stop>
            <stop offset=".45" stopColor="#38bdf8">
              <animate attributeName="stop-color" values="#38bdf8;#8b5cf6;#22d3ee;#38bdf8" dur="5s" repeatCount="indefinite" />
            </stop>
            <stop offset="1" stopColor="#2563eb">
              <animate attributeName="stop-color" values="#2563eb;#6d28d9;#0891b2;#2563eb" dur="5s" repeatCount="indefinite" />
            </stop>
          </radialGradient>
        )}
        <filter id={blur}><feGaussianBlur stdDeviation="3" /></filter>
        {mode === 'talking' && <clipPath id={clip}><circle cx="48" cy="48" r="30" /></clipPath>}
      </defs>

      {/* idle: anel externo respirando junto com o orb */}
      {mode === 'idle' && (
        <circle cx="48" cy="48" r="40" fill="none" stroke="#8b93a7" strokeWidth="1.5">
          <animate attributeName="r" values="40;44;40" dur="4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values=".35;.08;.35" dur="4s" repeatCount="indefinite" />
        </circle>
      )}

      {/* gravando: 2 ripples expandindo */}
      {mode === 'gravando' && (
        <>
          <circle cx="48" cy="48" r="28" fill="none" stroke="rgba(52,211,153,.7)" strokeWidth="1.5">
            <animate attributeName="r" values="28;46" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx="48" cy="48" r="28" fill="none" stroke="rgba(110,231,183,.7)" strokeWidth="1.5" opacity="0">
            <animate attributeName="r" values="28;46" dur="1.6s" begin="0.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0" dur="1.6s" begin="0.8s" repeatCount="indefinite" />
          </circle>
        </>
      )}

      {/* working: anel tracejado + arco girando em sentidos opostos */}
      {mode === 'working' && (
        <>
          <circle cx="48" cy="48" r="38" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="10 11" strokeLinecap="round" opacity=".9">
            <animateTransform attributeName="transform" type="rotate" from="0 48 48" to="360 48 48" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="48" cy="48" r="29" fill="none" stroke="#fbbf24" strokeWidth="2" strokeDasharray="36 146" strokeLinecap="round" opacity=".9">
            <animateTransform attributeName="transform" type="rotate" from="360 48 48" to="0 48 48" dur="1.6s" repeatCount="indefinite" />
          </circle>
        </>
      )}

      {/* recusado: anel piscando rápido — alerta de comando descartado */}
      {mode === 'recusado' && (
        <circle cx="48" cy="48" r="36" fill="none" stroke="#ef4444" strokeWidth="2.5" opacity=".9">
          <animate attributeName="opacity" values=".9;.15;.9" dur="0.55s" repeatCount="indefinite" />
        </circle>
      )}

      {/* talking: 2 ripples azuis */}
      {mode === 'talking' && (
        <>
          <circle cx="48" cy="48" r="28" fill="none" stroke="rgba(56,189,248,.7)" strokeWidth="1.5">
            <animate attributeName="r" values="28;46" dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="48" cy="48" r="28" fill="none" stroke="rgba(125,211,252,.7)" strokeWidth="1.5" opacity="0">
            <animate attributeName="r" values="28;46" dur="1.4s" begin="0.7s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".6;0" dur="1.4s" begin="0.7s" repeatCount="indefinite" />
          </circle>
        </>
      )}

      {/* orb central (raio, gradiente e respiração variam por modo) */}
      {mode === 'idle' && (
        <g style={{ filter: 'drop-shadow(0 0 8px rgba(107,114,128,.35))' }}>
          <circle cx="48" cy="48" r="26" fill={`url(#${grad})`}>
            <animate attributeName="r" values="26;28;26" dur="4s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      {mode === 'gravando' && (
        <g style={{ filter: 'drop-shadow(0 0 8px rgba(52,211,153,.6))' }}>
          <circle cx="48" cy="48" r="26" fill={`url(#${grad})`}>
            <animate attributeName="r" values="26;28;26" dur="2.2s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      {mode === 'working' && (
        <g style={{ filter: 'drop-shadow(0 0 8px rgba(251,191,36,.65))' }}>
          <circle cx="48" cy="48" r="22" fill={`url(#${grad})`}>
            <animate attributeName="r" values="22;23.8;22" dur="0.9s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      {mode === 'talking' && (
        <g style={{ filter: 'drop-shadow(0 0 8px rgba(56,189,248,.6))' }}>
          <circle cx="48" cy="48" r="30" fill={`url(#${grad})`} />
        </g>
      )}
      {mode === 'recusado' && (
        <g style={{ filter: 'drop-shadow(0 0 8px rgba(239,68,68,.6))' }}>
          <circle cx="48" cy="48" r="24" fill={`url(#${grad})`}>
            <animate attributeName="r" values="24;25.4;24" dur="1.1s" repeatCount="indefinite" />
          </circle>
        </g>
      )}

      {/* brilho elíptico (menor no working, maior no talking — spec) */}
      {mode === 'working'
        ? <ellipse cx="41.4" cy="39.64" rx="10.1" ry="6.6" fill="rgba(255,255,255,.22)" filter={`url(#${blur})`} />
        : mode === 'talking'
          ? <ellipse cx="39" cy="36" rx="13" ry="9" fill="rgba(255,255,255,.22)" filter={`url(#${blur})`} />
          : <ellipse cx="40.2" cy="38.12" rx="12" ry="7.8" fill="rgba(255,255,255,.22)" filter={`url(#${blur})`} />}

      {/* talking: ondas senoidais brancas clipadas no círculo */}
      {mode === 'talking' && (
        <g clipPath={`url(#${clip})`}>
          <path
            d="M0 48 Q7.5 39 15 48 Q22.5 57 30 48 Q37.5 39 45 48 Q52.5 57 60 48 Q67.5 39 75 48 Q82.5 57 90 48 Q97.5 39 105 48 Q112.5 57 120 48 Q127.5 39 135 48 Q142.5 57 150 48 Q157.5 39 165 48 Q172.5 57 180 48"
            fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2.5" strokeLinecap="round"
          >
            <animateTransform attributeName="transform" type="translate" values="0 0;-60 0" dur="1.2s" repeatCount="indefinite" />
            <animate attributeName="stroke-width" values="2.5;1.6;2.5" dur="1.6s" repeatCount="indefinite" />
          </path>
          <g opacity=".5">
            <path
              d="M0 48 Q7.5 42 15 48 Q22.5 54 30 48 Q37.5 42 45 48 Q52.5 54 60 48 Q67.5 42 75 48 Q82.5 54 90 48 Q97.5 42 105 48 Q112.5 54 120 48 Q127.5 42 135 48 Q142.5 54 150 48 Q157.5 42 165 48 Q172.5 54 180 48"
              fill="none" stroke="rgba(255,255,255,.8)" strokeWidth="2" strokeLinecap="round"
            >
              <animateTransform attributeName="transform" type="translate" values="-60 0;0 0" dur="0.9s" repeatCount="indefinite" />
            </path>
          </g>
        </g>
      )}
    </svg>
  )
}
